import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { signupApi, customerApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Public signup page. No auth required.
 *
 * Two account types:
 *   - HOMEGROUP: signing up on behalf of a homegroup. Pick existing or
 *     request new. Requires lit_rep or GSR name (at least one).
 *   - PERSONAL: signing up for personal literature orders. Just basic
 *     identity fields — no homegroup picker, no lit/GSR roles.
 *
 * The account-type radio at the top drives which fields appear. Submission
 * either way goes to /api/signup; the request lands in the admin queue and
 * an email goes to the literature chair.
 */
export default function Signup() {
    const navigate = useNavigate()

    const [customers, setCustomers] = useState([])
    const [loadingCustomers, setLoadingCustomers] = useState(true)

    // Account type — homegroup or personal
    const [accountType, setAccountType] = useState('homegroup')

    // Common form state
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [city, setCity] = useState('')

    // Homegroup-only form state
    const [litRepName, setLitRepName] = useState('')
    const [gsrName, setGsrName] = useState('')
    const [requestNew, setRequestNew] = useState(false)
    const [customerId, setCustomerId] = useState('')
    const [requestedCustomerName, setRequestedCustomerName] = useState('')

    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    useEffect(() => {
        loadCustomers()
    }, [])

    async function loadCustomers() {
        try {
            const response = await customerApi.list(false)
            if (response.success) {
                // Only show homegroups in the picker — personal signups don't pick,
                // subcommittees/area are admin-managed.
                const homegroupsOnly = (response.customers || []).filter(c => c.kind === 'homegroup')
                setCustomers(homegroupsOnly)
            }
        } catch {
            // Non-fatal — they can still sign up by requesting a new customer.
        } finally {
            setLoadingCustomers(false)
        }
    }

    async function handleSubmit(e) {
        e.preventDefault()

        // ---- Common validation ----
        if (!email.trim() || !password || !name.trim()) {
            toast.error('Email, password, and name are required')
            return
        }
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters')
            return
        }

        // ---- Homegroup-only validation ----
        if (accountType === 'homegroup') {
            if (!requestNew && !customerId) {
                toast.error('Please choose your homegroup or tick "request new"')
                return
            }
            if (requestNew && !requestedCustomerName.trim()) {
                toast.error('Please enter the homegroup name')
                return
            }
            if (!litRepName.trim() && !gsrName.trim()) {
                toast.error('Please tell us who your lit rep or GSR is (at least one).')
                return
            }
        }

        const payload = {
            email: email.trim(),
            password,
            name: name.trim(),
            accountType,
            phone: phone || null,
            city: city || null,
        }

        if (accountType === 'homegroup') {
            payload.customerId = requestNew ? null : Number(customerId)
            payload.requestedCustomerName = requestNew ? requestedCustomerName.trim() : null
            payload.requestedCustomerKind = requestNew ? 'homegroup' : null
            payload.litRepName = litRepName.trim() || null
            payload.gsrName = gsrName.trim() || null
        }
        // For personal: send only basic fields; server ignores the rest.

        setSubmitting(true)
        try {
            const response = await signupApi.submit(payload)
            if (response.success) {
                setSubmitted(true)
            } else {
                toast.error(response.message || 'Submission failed')
            }
        } catch {
            toast.error('Network error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    // ----- Submitted screen ----------------------------------------------------
    if (submitted) {
        return (
            <Shell>
                <div className="text-center space-y-4">
                    <div className="text-4xl">✓</div>
                    <h2 className="text-xl font-semibold text-ghasc-navy">Request submitted</h2>
                    <p className="text-gray-600 text-sm">
                        Thanks! The literature chair will review your request and email you
                        once it's approved. After that, you can log in and start placing orders.
                    </p>
                    <div className="pt-2">
                        <Button variant="secondary" onClick={() => navigate('/login')}>
                            Back to login
                        </Button>
                    </div>
                </div>
            </Shell>
        )
    }

    // ----- Form ---------------------------------------------------------------
    const isPersonal = accountType === 'personal'

    return (
        <Shell>
            <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-xl font-semibold text-ghasc-navy">Sign up</h2>
                <p className="text-sm text-gray-500">
                    The literature chair will review your request and email you once approved.
                </p>

                {/* Account type radio */}
                <fieldset className="border border-gray-200 rounded-lg p-3 space-y-2">
                    <legend className="text-sm font-medium text-gray-700 px-1">Account type</legend>
                    <label className="flex items-start text-sm cursor-pointer">
                        <input
                            type="radio"
                            checked={accountType === 'homegroup'}
                            onChange={() => setAccountType('homegroup')}
                            className="mr-2 mt-1"
                        />
                        <span>
                            <span className="font-medium">Homegroup</span>
                            <span className="block text-xs text-gray-500">
                                I'm placing orders on behalf of an NA homegroup.
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start text-sm cursor-pointer">
                        <input
                            type="radio"
                            checked={accountType === 'personal'}
                            onChange={() => setAccountType('personal')}
                            className="mr-2 mt-1"
                        />
                        <span>
                            <span className="font-medium">Personal</span>
                            <span className="block text-xs text-gray-500">
                                Just for me — I want to buy literature for myself, sponsees, etc.
                            </span>
                        </span>
                    </label>
                </fieldset>

                {/* Common identity fields */}
                <Input id="signup-email" label="Email" type="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    required autoFocus autoComplete="email" />

                <Input id="signup-password" label="Password (8+ characters)" type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    required autoComplete="new-password" />

                <Input id="signup-name" label="Your name"
                    value={name} onChange={(e) => setName(e.target.value)} required />

                <div className="grid sm:grid-cols-2 gap-3">
                    <Input id="signup-city" label="City (optional)"
                        value={city} onChange={(e) => setCity(e.target.value)} />
                    <Input id="signup-phone" label="Phone (optional)"
                        value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>

                {/* ===== Homegroup-only fields ===== */}
                {!isPersonal && (
                    <>
                        <div className="border-t border-gray-100 pt-3">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 mb-3">
                                <p className="font-medium">Homegroup roles</p>
                                <p className="text-xs mt-0.5">
                                    Tell us who's doing what. At least one is required. The literature
                                    chair can adjust these on approval.
                                </p>
                            </div>

                            <Input id="signup-lit-rep" label="Lit Rep Name (places orders)"
                                value={litRepName}
                                onChange={(e) => setLitRepName(e.target.value)}
                                placeholder="Optional if you have a GSR name below" />

                            <div className="mt-3">
                                <Input id="signup-gsr" label="GSR Name (submits monthly reports)"
                                    value={gsrName}
                                    onChange={(e) => setGsrName(e.target.value)}
                                    placeholder="Optional if you have a lit rep name above" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Homegroup</label>
                            <select
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                disabled={requestNew || loadingCustomers}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">
                                    {loadingCustomers ? 'Loading...' : '— Choose your homegroup —'}
                                </option>
                                {customers.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-center text-sm">
                            <input
                                type="checkbox"
                                checked={requestNew}
                                onChange={(e) => {
                                    setRequestNew(e.target.checked)
                                    if (e.target.checked) setCustomerId('')
                                }}
                                className="mr-2"
                            />
                            My homegroup isn't listed — request a new one
                        </label>

                        {requestNew && (
                            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
                                <Input id="signup-new-name" label="New homegroup name"
                                    value={requestedCustomerName}
                                    onChange={(e) => setRequestedCustomerName(e.target.value)}
                                    placeholder="e.g. Key to Freedom"
                                    required={requestNew} />
                                <p className="text-xs text-gray-500">
                                    The literature chair will confirm or correct the name when reviewing.
                                    Subcommittee and area-officer accounts are created directly by the chair.
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* ===== Personal info banner ===== */}
                {isPersonal && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                        <p className="font-medium">Personal account</p>
                        <p className="text-xs mt-0.5">
                            You'll be able to shop and check out for your own literature.
                            Pickup is at the area meeting on pickup day, same as homegroups.
                        </p>
                    </div>
                )}

                <Button type="submit" loading={submitting} className="w-full">
                    Submit request
                </Button>

                <p className="text-center text-sm text-gray-500 pt-2">
                    Already have an account? <Link to="/login" className="text-ghasc-navy hover:underline">Log in</Link>
                </p>
            </form>
        </Shell>
    )
}

function Shell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ghasc-navy-dark to-ghasc-navy px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold text-ghasc-navy">GHASC Literature</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Georgian Heartland Area Service Committee
                    </p>
                </div>
                {children}
            </div>
        </div>
    )
}